import { addDays, addMonths, addYears, differenceInCalendarDays, format, isValid, parse, parseISO, startOfDay } from "date-fns";

/** Legacy MySQL zero-dates and empty strings → treat as blank in the UI. */
export function isEmptyDueDate(value: unknown): boolean {
  if (value == null) return true;
  if (value instanceof Date) return !isValid(value);
  if (typeof value !== "string") return false;
  const s = value.trim();
  return s === "" || s.startsWith("0000-00-00");
}

/** Coerce API date strings to `Date` for formik-mui DatePicker. */
export function sanitizeDueDateForForm(value: unknown): Date | null {
  if (isEmptyDueDate(value)) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const parsed = parseISO(trimmed.slice(0, 10));
      return isValid(parsed) ? parsed : null;
    }
    for (const fmt of ["dd-MMM-yyyy", "dd/MM/yyyy", "yyyy-MM-dd"]) {
      const parsed = parse(trimmed, fmt, new Date());
      if (isValid(parsed)) return parsed;
    }
    const fallback = new Date(trimmed);
    return isValid(fallback) ? fallback : null;
  }
  return null;
}

/** Serialize Formik date values to `yyyy-MM-dd` for the API. */
export function sanitizeDueDateForApi(
  value: unknown,
  displayFormat?: string
): string | null {
  if (isEmptyDueDate(value)) return null;
  if (value instanceof Date) {
    return isValid(value) ? format(value, "yyyy-MM-dd") : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.includes("T") || /^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const parsed = parseISO(trimmed.slice(0, 10));
      return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
    }
    if (displayFormat) {
      const parsed = parse(trimmed, displayFormat, new Date());
      if (isValid(parsed)) return format(parsed, "yyyy-MM-dd");
    }
    for (const fmt of ["dd-MMM-yyyy", "dd/MM/yyyy", "yyyy-MM-dd"]) {
      const parsed = parse(trimmed, fmt, new Date());
      if (isValid(parsed)) return format(parsed, "yyyy-MM-dd");
    }
    const fallback = new Date(trimmed);
    return isValid(fallback) ? format(fallback, "yyyy-MM-dd") : null;
  }
  return null;
}

/** Days from today until due date (negative if overdue). Matches Roxen Remaining. */
export function daysRemainingUntilDue(value: unknown): number | null {
  const due = sanitizeDueDateForForm(value);
  if (!due) return null;
  return differenceInCalendarDays(startOfDay(due), startOfDay(new Date()));
}

export type PeriodOffset = {
  amount: number;
  unit: "days" | "months" | "years";
};

/** Parse labels like "3 Month", "1 Year", "30 Days" (Roxen period types). */
export function parsePeriodOffset(label: string): PeriodOffset | null {
  const normalized = label.trim().toLowerCase();
  const match = normalized.match(
    /^(\d+(?:\.\d+)?)\s*(day|days|month|months|year|years)$/
  );
  if (match) {
    const amount = parseFloat(match[1]);
    const unitWord = match[2];
    if (unitWord.startsWith("day")) return { amount, unit: "days" };
    if (unitWord.startsWith("month")) return { amount, unit: "months" };
    return { amount, unit: "years" };
  }
  const numMatch = normalized.match(/^(\d+(?:\.\d+)?)$/);
  if (numMatch) {
    return { amount: parseFloat(numMatch[1]), unit: "months" };
  }
  return null;
}

export function calculateDueDateFromLastDoneAndPeriod(
  lastDone: unknown,
  periodLabel: string
): Date | null {
  const base = sanitizeDueDateForForm(lastDone);
  if (!base) return null;
  const offset = parsePeriodOffset(periodLabel);
  if (!offset) return null;
  if (offset.unit === "days") return addDays(base, offset.amount);
  if (offset.unit === "months") return addMonths(base, offset.amount);
  return addYears(base, offset.amount);
}

export function resolvePeriodLabel(
  periodValue: string | number | null | undefined,
  periodSelectOptions: { value: string; label: string }[],
  periodConfigMap: Record<string, string> | null
): string {
  if (periodValue == null || periodValue === "") return "";
  const key = String(periodValue);
  const fromOptions = periodSelectOptions.find((o) => o.value === key);
  if (fromOptions?.label) return fromOptions.label;
  if (periodConfigMap?.[key]) return periodConfigMap[key];
  return key;
}
